import React, { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@shadcn-in-nx/ui/table";
import { Input } from "@shadcn-in-nx/ui/input";
import { Search, X, LayoutGrid, List, ExternalLink } from "lucide-react";
import { useBluelagoon } from "@bluelagoon";
import { formatDate } from "@utils";
import { useNavigate } from "react-router-dom";
import Icon from "../components/Icon";
import Pagination from "../components/Pagination";
import { Skeleton } from "@shadcn-in-nx/ui/skeleton";
import FavoriteButton from "../components/FavoriteButton";
import useFavorites from "../Apps/Workflow/hooks/useFavorites";
import DashboardWorkflowCard from "../Dashboard/DashboardWorkflowCard";

const VIEW_MODE_KEY = "clientWorkflows.viewMode";

export function ClientWorkflows() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isFavorite, reloadFavorites } = useFavorites();
    const [viewMode, setViewMode] = useState(() => {
        if (typeof window === "undefined") return "grid";
        return window.localStorage.getItem(VIEW_MODE_KEY) === "list"
            ? "list"
            : "grid";
    });

    useEffect(() => {
        if (typeof window !== "undefined") {
            window.localStorage.setItem(VIEW_MODE_KEY, viewMode);
        }
    }, [viewMode]);

    const [workflows, setWorkflows] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 30;
    const [pagination, setPagination] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const requestSeqRef = useRef(0);
    const prevSearchRef = useRef("");

    const { call } = useBluelagoon({
        method: "workflowsList",
    });

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        // When the search term changes while not on page 1, reset the page and
        // skip this run's fetch — the page reset will retrigger this effect
        // and fire a single fetch for page 1 of the new query. Without this
        // guard, the page-old + page-1 fetches race and can leave the UI
        // showing rows from a different page than the pagination control.
        if (
            prevSearchRef.current !== debouncedSearchTerm &&
            currentPage !== 1
        ) {
            prevSearchRef.current = debouncedSearchTerm;
            setCurrentPage(1);
            return;
        }
        prevSearchRef.current = debouncedSearchTerm;
        loadWorkflows(currentPage);
    }, [currentPage, debouncedSearchTerm]);

    const loadWorkflows = useCallback(
        async (currentPage = 1) => {
            const seq = ++requestSeqRef.current;
            try {
                // Only show full loading on initial load
                if (initialLoading) {
                    setInitialLoading(true);
                } else {
                    setLoading(true);
                }
                setError(null);
                const response = await call({
                    limit: itemsPerPage,
                    offset: (currentPage - 1) * itemsPerPage,
                    status: "ACTIVE", // Only show active workflows for clients
                    search: debouncedSearchTerm ? debouncedSearchTerm : "",
                    published: "true",
                });

                if (seq !== requestSeqRef.current) return;
                if (response?.workflows) {
                    setWorkflows(response.workflows);
                    setPagination(response.pagination);
                }
            } catch (err) {
                if (seq !== requestSeqRef.current) return;
                console.error("Error loading workflows:", err);
                setError(t("clientWorkflows.failedToLoad"));
            } finally {
                if (seq === requestSeqRef.current) {
                    setInitialLoading(false);
                    setLoading(false);
                }
            }
        },
        [call, itemsPerPage, debouncedSearchTerm, initialLoading]
    );

    const handleWorkflowClick = (workflow) => {
        if (workflow.internalDomain && workflow.workflowType === "APP_V2") {
            navigate(`/client/app2/${workflow.internalDomain}`);
        } else {
            navigate(`/client/workflows/${workflow.publicKey}`);
        }
    };

    const cardSkeletons = (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
                <div
                    key={i}
                    className="border border-gray-100 rounded-lg overflow-hidden"
                >
                    <Skeleton className="w-full aspect-video" />
                    <div className="p-3 space-y-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                    </div>
                </div>
            ))}
        </div>
    );

    if (initialLoading) {
        return (
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <Skeleton className="h-8 w-[150px]" />
                </div>
                <div className="flex items-center justify-between border-b pb-4">
                    <Skeleton className="h-5 w-[120px]" />
                </div>
                {cardSkeletons}
            </div>
        );
    }

    const buildClientLink = (workflow) =>
        workflow.internalDomain && workflow.workflowType === "APP_V2"
            ? `/client/app2/${workflow.internalDomain}`
            : `/client/workflows/${workflow.publicKey}`;

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-xl sm:text-2xl font-bold tracking-tight">
                    <span className="hidden sm:inline">
                        {t("clientWorkflows.title")}
                    </span>
                    <span className="sm:hidden">
                        {t("clientWorkflows.titleShort")}
                    </span>
                </h2>
            </div>

            {/* Search bar + view toggle */}
            <div className="flex items-center gap-4 pb-4">
                <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                        type="text"
                        placeholder={t("clientWorkflows.searchPlaceholder")}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-10"
                    />
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm("")}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600"
                            aria-label={t("common.clear")}
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>
                <div
                    role="group"
                    aria-label={t("clientWorkflows.viewMode")}
                    className="ml-auto inline-flex items-center rounded-md border border-gray-200 bg-white p-0.5"
                >
                    <button
                        type="button"
                        onClick={() => setViewMode("grid")}
                        aria-pressed={viewMode === "grid"}
                        aria-label={t("clientWorkflows.gridView")}
                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-sm ${
                            viewMode === "grid"
                                ? "bg-gray-100 text-gray-900"
                                : "text-gray-500 hover:text-gray-700"
                        }`}
                    >
                        <LayoutGrid className="h-4 w-4" />
                        <span className="hidden sm:inline">
                            {t("clientWorkflows.gridView")}
                        </span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setViewMode("list")}
                        aria-pressed={viewMode === "list"}
                        aria-label={t("clientWorkflows.listView")}
                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-sm ${
                            viewMode === "list"
                                ? "bg-gray-100 text-gray-900"
                                : "text-gray-500 hover:text-gray-700"
                        }`}
                    >
                        <List className="h-4 w-4" />
                        <span className="hidden sm:inline">
                            {t("clientWorkflows.listView")}
                        </span>
                    </button>
                </div>
            </div>

            <div className="flex items-center justify-between border-b pb-4">
                <div className="text-xs sm:text-sm text-muted-foreground">
                    {!loading &&
                        t("clientWorkflows.availableCount", {
                            count: workflows.length,
                        })}
                    <span className="hidden sm:inline">
                        {" "}
                        {t("clientWorkflows.appsAndAutomations")}
                    </span>
                    {debouncedSearchTerm && (
                        <span className="hidden sm:inline">
                            {" "}
                            {t("clientWorkflows.matchingQuery", {
                                query: debouncedSearchTerm,
                            })}
                        </span>
                    )}
                </div>
            </div>

            {error && (
                <div className="text-red-500 p-4 bg-red-50 rounded">
                    {error}
                </div>
            )}

            {loading ? (
                cardSkeletons
            ) : workflows.length === 0 ? (
                <div className="text-center p-12 rounded-lg border-2 border-dashed border-gray-300 bg-white">
                    <div className="flex flex-col items-center gap-3">
                        <div className="rounded-full bg-gray-50 p-4">
                            <Icon
                                name="LayoutGrid"
                                className="h-10 w-10 text-gray-400"
                            />
                        </div>
                        <h3 className="mt-2 text-base font-semibold text-gray-900">
                            {debouncedSearchTerm
                                ? t("clientWorkflows.noResultsForQuery", {
                                      query: debouncedSearchTerm,
                                  })
                                : t("clientWorkflows.noResults")}
                        </h3>
                        <p className="text-sm text-gray-500 max-w-sm">
                            {debouncedSearchTerm
                                ? t("clientWorkflows.noResultsHint")
                                : t("clientWorkflows.emptyHint")}
                        </p>
                    </div>
                </div>
            ) : (
                <div className="space-y-4">
                    {viewMode === "grid" ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {workflows.map((workflow) => (
                                <DashboardWorkflowCard
                                    key={workflow.id}
                                    workflow={workflow}
                                    to={buildClientLink(workflow)}
                                    isFavorite={isFavorite(workflow.id)}
                                    onFavoriteChange={reloadFavorites}
                                />
                            ))}
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-8" />
                                    <TableHead>
                                        {t("clientWorkflows.columnName")}
                                    </TableHead>
                                    <TableHead className="hidden md:table-cell">
                                        {t("clientWorkflows.columnCreated")}
                                    </TableHead>
                                    <TableHead className="w-1">
                                        {t("clientWorkflows.columnAccess")}
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {workflows.map((workflow) => (
                                    <TableRow
                                        key={workflow.id}
                                        className="cursor-pointer hover:bg-gray-50"
                                        onClick={() =>
                                            handleWorkflowClick(workflow)
                                        }
                                    >
                                        <TableCell className="w-8">
                                            <FavoriteButton
                                                workflowId={workflow.id}
                                                isFavorite={isFavorite(
                                                    workflow.id
                                                )}
                                                onFavoriteChange={
                                                    reloadFavorites
                                                }
                                                size={14}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <span className="text-blue-600 text-base font-medium hover:underline">
                                                {workflow.name}
                                            </span>
                                        </TableCell>
                                        <TableCell className="hidden md:table-cell text-sm text-gray-500">
                                            {formatDate(
                                                new Date(workflow.createdAt)
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end">
                                                {workflow.type === "APP" &&
                                                workflow.published ? (
                                                    <ExternalLink className="h-4 w-4 text-gray-400" />
                                                ) : (
                                                    <Icon
                                                        name="Eye"
                                                        className="h-4 w-4 text-gray-400"
                                                    />
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                    {pagination && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={Math.ceil(
                                pagination.total / itemsPerPage
                            )}
                            onPageChange={setCurrentPage}
                            itemsPerPage={itemsPerPage}
                            totalItems={pagination.total}
                            className="pt-4"
                        />
                    )}
                </div>
            )}
        </div>
    );
}
